import React from 'react';
import i18n from '../../../../i18n';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import FilterSection from '../../../common/Filter';
import DateRangeSelector from '../../../common/DateRange';
import { getClientDashboard } from '../../../../models/management';
import {
  Activity,
  ChartLine,
  Clock,
  Gavel,
  ReceiptText,
  RotateCw,
  Trophy,
  Users,
} from 'lucide-react';
import ProjectCardSkeleton from '../../../common/skelton/ProjectCardSkelton';
import ProjectCard from '../../../common/ProjectCard';
import VatCard from '../../../common/VatCard';
import { NavLink } from 'react-router-dom';
import '../../../../styles/management-report.scss';
import { DELIVERY_STATUS, DELIVERY_STATUS_ICONS } from '../../../../constants/status';
import AuctionOverview from '../Management/components/AuctionOverview';
import '../../../../styles/dashboard.scss';

export class ClientDashboard extends React.Component {
  constructor() {
    super();
    const today = new Date();
    const last7 = new Date();
    last7.setDate(today.getDate() - 7);

    this.state = {
      search: {
        startDate: last7.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      },
      vatSummary: null,
      projectSummary: null,
      isLoading: false,
      clearDate: null,
      auctionOverviewCount: {
        enrollmentBidders: 0,
        strategyEnrollments: 0,
        previousAuctions: 0,
        runningAuctions: 0,
        totalWinners: 0,
      },
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
  }
  componentDidMount() {
    window.scrollTo(0, 0);
    // Parse URL query params
    const params = new URLSearchParams(window.location.search);
    const search = {
      startDate: params.get('startDate') || this.state.search.startDate,
      endDate: params.get('endDate') || this.state.search.endDate,
    };

    this.setState({ search }, () => {
      this.loadDashboardSummary();
    });
  }

  // Update URL without refreshing page
  updateUrlQuery = (query) => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(query).filter(([_, v]) => v))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  };

  async loadDashboardSummary() {
    this.setState({ isLoading: true });
    let { startDate, endDate } = this.state.search;

    const q = { ...(startDate && { startDate }), ...(endDate && { endDate }) };
    this.updateUrlQuery(q);

    try {
      const response = await getClientDashboard(q);
      if (response.success) {
        const { auctionOverview, project_summary, vat_summary } = response.data;

        this.setState({
          auctionOverviewCount: {
            enrollmentBidders: auctionOverview.enrollment_count || 0,
            previousAuctions: auctionOverview.previous_auciton_count || 0,
            runningAuctions: auctionOverview.running_auction_count || 0,
            totalWinners: auctionOverview.total_winners_count || 0,
          },
          projectSummary: project_summary,
          vatSummary: vat_summary,
        });
      }
    } catch (error) {
      console.error('Dashboard load failed:', error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => {
      const updatedSearch = {
        ...prevState.search,
        [name]: value,
      };
      return { search: updatedSearch };
    });
  };

  handleSearch = () => {
    this.loadDashboardSummary();
  };

  handleClear = () => {
    const today = new Date();
    const last7 = new Date();
    last7.setDate(today.getDate() - 7);

    const defaultSearch = {
      startDate: last7.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };

    this.setState(
      {
        search: defaultSearch,
        clearDate: Date.now(), // Trigger DateRangeSelector reset
      },
      () => {
        this.loadDashboardSummary();
      }
    );
  };

  handleDateRangeChange = (dateRange) => {
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          startDate: dateRange.startDate, // Will handle null correctly
          endDate: dateRange.endDate, // Will handle null correctly
        },
      }),
      () => {
        // Callback ensures state is updated before fetching
        this.loadDashboardSummary();
      }
    );
  };

  render() {
    const { auctionOverviewCount, isLoading } = this.state;
    const auctionMetrics = [
      {
        label: 'Enrollment Bidders',
        value: auctionOverviewCount.enrollmentBidders,
        icon: Users,
        color: 'blue',
        path: '/enrollment-overview',
        type: null,
      },
      {
        label: 'Previous Auctions',
        value: auctionOverviewCount.previousAuctions,
        icon: Clock,
        color: 'gray',
        path: '/auction-overview',
        status: 'E',
      },
      {
        label: 'Running Auctions',
        value: auctionOverviewCount.runningAuctions,
        icon: Activity,
        color: 'orange',
        path: '/auction-overview',
        status: 'A',
      },
      {
        label: 'Total Winners',
        value: auctionOverviewCount.totalWinners,
        icon: Trophy,
        color: 'gold',
        path: '/winner-overview',
      },
    ];
    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('client_dashboard')}
          subtitle={i18n.t('client_dashboard_subtitle')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({
                            hideSidebar: !this.state.hideSidebar,
                          });
                        }}
                      >
                        {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                        {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                    {/* refresh Button */}
                    <button
                      className="button1 button1-gradient"
                      onClick={() => {
                        this.loadDashboardSummary();
                      }}
                      disabled={isLoading}
                    >
                      <RotateCw className={`refresh-icon ${isLoading ? 'spinning' : ''}`} />
                      {i18n.t('refresh')}
                    </button>
                  </div>
                  <div className="card-body">
                    <FilterSection
                      loading={this.state.isLoading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      {/* Start Date */}
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          value={this.state.search.startDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="startDate" className="floating-label">
                          {i18n.t('start_date')}
                        </label>
                      </div>

                      {/* End Date */}
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          value={this.state.search.endDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder={i18n.t('end_date')}
                        />
                        <label htmlFor="endDate" className="floating-label">
                          {i18n.t('end_date')}
                        </label>
                      </div>

                      <DateRangeSelector
                        onChange={this.handleDateRangeChange}
                        clearTrigger={this.state.clearDate}
                      />
                    </FilterSection>
                    <div className="management-report">
                      <AuctionOverview
                        loading={isLoading}
                        metricsCount={4}
                        metrics={auctionMetrics}
                        startDate={this.state.search.startDate}
                        endDate={this.state.search.endDate}
                      />

                      <div className="report-item">
                        <div className="report-item-header">
                          {/* <p className="subtitle">Project Summary</p> */}
                          <h2 className="overview-title">Project Summary</h2>
                        </div>
                        <div className="report-item-content">
                          {this.state.isLoading ? (
                            <>
                              {/* Skeleton Cards */}
                              {[...Array(9)].map((_, index) => (
                                <ProjectCardSkeleton key={index} />
                              ))}
                            </>
                          ) : (
                            <>
                              {this.state.projectSummary?.map((project, index) => {
                                const Icon =
                                  DELIVERY_STATUS_ICONS[project.delivery_status] || Clock; // fallback icon
                                const deliveryParams =
                                  project.delivery_status === 1
                                    ? `deliveryStatus=${project.delivery_status}&status=E`
                                    : `deliveryStatus=${project.delivery_status}`;

                                return (
                                  <NavLink key={index} to={`/auction-overview?${deliveryParams}`}>
                                    <ProjectCard
                                      className={DELIVERY_STATUS[project.delivery_status].replace(
                                        /\s+/g,
                                        ''
                                      )}
                                      icon={Icon}
                                      title={DELIVERY_STATUS[project.delivery_status]}
                                      value={project.auctions_count || 0}
                                      amount={project.final_bid_amount || 0}
                                    />
                                  </NavLink>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="report-item">
                        <div className="report-item-header">
                          <h2 className="overview-title">Profit & Vat Summary</h2>
                        </div>
                        <div className="report-item-content">
                          {this.state.isLoading ? (
                            <>
                              {/* Skeleton Cards */}
                              {[...Array(3)].map((_, index) => (
                                <ProjectCardSkeleton key={index} />
                              ))}
                            </>
                          ) : (
                            <>
                              <VatCard
                                className="service-charge"
                                icon={ChartLine}
                                title="Bid Amount"
                                value={this.state.vatSummary?.bid_amount.value || 0}
                                trend={this.state.vatSummary?.bid_amount.trend}
                                percentage={this.state.vatSummary?.bid_amount.percentage || 0}
                                timeframe={this.state.vatSummary?.bid_amount.timeframe}
                              />
                              <VatCard
                                className="service-charge"
                                icon={ChartLine}
                                title="Service Charge"
                                value={this.state.vatSummary?.service_charge.value || 0}
                                trend={this.state.vatSummary?.service_charge.trend}
                                percentage={this.state.vatSummary?.service_charge.percentage || 0}
                                timeframe={this.state.vatSummary?.service_charge.timeframe}
                              />

                              <VatCard
                                className="vat-service"
                                icon={ReceiptText}
                                title="Service Charge VAT"
                                value={this.state.vatSummary?.service_charge_vat.value || 0}
                                trend={this.state.vatSummary?.service_charge_vat.trend}
                                percentage={
                                  this.state.vatSummary?.service_charge_vat.percentage || 0
                                }
                                timeframe={this.state.vatSummary?.service_charge_vat.timeframe}
                              />
                              <VatCard
                                className="vat-bid"
                                icon={Gavel}
                                title="Bid Amount VAT"
                                value={this.state.vatSummary?.bid_amount_vat.value || 0}
                                trend={this.state.vatSummary?.bid_amount_vat.trend}
                                percentage={this.state.vatSummary?.bid_amount_vat.percentage || 0}
                                timeframe={this.state.vatSummary?.bid_amount_vat.timeframe}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}
